// eslint-disable-next-line max-classes-per-file
import { winTypes } from '../../../enums/winPopupTypes';
import { gameMainDrawer } from '../../../game/drawers/gameMainDrawer';

import { gameEvents } from '../../../lib/gameEvents';
import { addToGameLoop, getGameLastTime, removeFromGameLoop } from '../../../lib/gameLoop';

import { digitLength, toFixed } from '../../../helpers/math';
import { getWinPopupType } from '../../../helpers/getWinPopupType';

import { winBarStore } from '../../../store/winBarStore';
import { spinBarStore } from '../../../store/spinBarStore';
import { menuBarStore } from '../../../store/menuBarStore';
import { addKeyboardListener } from '../../../core/helpers/keyboard';
import { gameStore } from '../../../store/gameStore';
import { addLoopToGameTimer, addToGameTimer, removeFromGameTimer } from '../../../lib/timer';
import { soundService, SoundTypes } from '../../baseSoundService/soundService';
import { isoStandard } from '../../../helpers/isoStandard';

const animationNames = {
  [winTypes.STANDARD]: 'win',
  [winTypes.BIG]: 'big',
  [winTypes.MEGA]: 'mega',
  [winTypes.SUPER]: 'super',
  [winTypes.EPIC]: 'epic',
};
const fastTimeScale = 5;
export class Signal {
  constructor() {
    this.listeners = [];
  }

  add(callback, context) {
    const index = this.listeners.findIndex((a) => a.callback === callback && a.context === context);
    if (index > -1) this.listeners[index] = { callback, context };
    else this.listeners.push({ callback, context });
  }

  addOnce(callback, context = null) {
    const wrapper = () => {
      this.remove(wrapper);
      callback.call(context);
    };
    this.add(wrapper);
  }

  remove(callback, context) {
    const index = this.listeners.findIndex((a) => a.callback === callback && a.context === context);
    if (index > -1) this.listeners.splice(index);
  }

  clear() {
    this.listeners.length = 0;
  }

  dispatch(...args) {
    this.listeners.forEach((a) => a.callback.call(a.context, ...args));
  }
}
class Animation {
  constructor(target) {
    this.target = target;
    this.percent = 0;
    this.min = 0;
    this.max = 1;
    this.duration = 0;
    this.timeScale = 1;
    this.loop = false;
    this.onComplete = new Signal();
    this.onLoop = new Signal();
    this.onStart = new Signal();
    this.playing = false;
  }

  prepare(options = {}, fromCurrentPosition = false) {
    this.duration = options.duration || 0;
    this.min = options.min || 0;
    this.max = options.max || 0;
    this.loop = !!options.loop;
    // this.easing = options.easing || Easing.Linear.None;
    this.biDirection = !!options.biDirection;
    this.percent = 0;
    if (fromCurrentPosition) {
      const currentValue = this.getter();
      this.percent = (currentValue - this.min) / (this.max - this.min);
      this.duration *= 1 - this.percent;
    }
    this.setter(this.min);
    this.direction = 1;
    this.loopCount = 0;
  }

  start(options, fromCurrentPosition) {
    this.direction = 1;
    this.prepare(options, fromCurrentPosition);
    this._lastTime = getGameLastTime();
    this.playing = true;
    this.onStart.dispatch();
    addToGameLoop(this._loop, this);
  }

  stop(emitComplete = false) {
    removeFromGameLoop(this._loop, this);
    this.playing = false;
    if (emitComplete) this.onComplete.dispatch();
  }

  _loop(time) {
    this.update(time - this._lastTime);
    this._lastTime = time;
  }

  update(deltaTime) {
    if (this.duration) {
      this.percent += (deltaTime / this.duration) * this.timeScale;
    } else this.percent = 1;
    if (this.percent >= 1) {
      if (this.loop) {
        // eslint-disable-next-line no-plusplus
        this.loopCount++;
        this.onLoop.dispatch();
        this.percent %= 1;
      } else {
        this.setter(this.biDirection ? this.min : this.max);
        removeFromGameLoop(this._loop, this);
        this.playing = false;
        this.onComplete.dispatch();
        return;
      }
    }
    let currentPercent = this.percent;
    if (this.biDirection) {
      // eslint-disable-next-line no-use-before-define
      const percentParts = splitPercent(currentPercent);
      this.direction = percentParts[0] > 0 ? -1 : 1;
      // eslint-disable-next-line prefer-destructuring
      currentPercent = percentParts[1];
    }
    // currentPercent = this.easing(currentPercent);
    if (this.direction === -1) currentPercent = 1 - currentPercent;
    this.setter(this.min + currentPercent * (this.max - this.min));
  }

  setter(val) {
    this._val = val;
  }

  getter() {
    return this._val;
  }
}
function splitPercent(percent) {
  const parts = (percent / 0.5).toString().split('.');
  if (parts.length === 1) parts.unshift(0);
  else parts[1] = `0.${parts[1]}`;
  return parts.map(Number);
}

const popupsDurationByType = {
  [winTypes.BIG]: 3000,
  [winTypes.MEGA]: 4000,
  [winTypes.SUPER]: 5000,
  [winTypes.EPIC]: 12000,
};
const popupsSoundsByType = {
  [winTypes.BIG]: SoundTypes.WinPopupBigWin,
  [winTypes.MEGA]: SoundTypes.WinPopupMegaWin,
  [winTypes.SUPER]: SoundTypes.WinPopupSuperWin,
  [winTypes.EPIC]: SoundTypes.WinPopupEpicWin,
};

export class WinPopup {
  constructor() {
    this.popup = gameMainDrawer.layers.winPopupBar.WinPopupContainer;
    this.popup.visible = false;
    this.blackayer = gameMainDrawer.layers.winPopupBar.winPopupBackLayer;
    this.spine = gameMainDrawer.layers.winPopupBar.winPopup;
    this.amount = gameMainDrawer.layers.winPopupBar.winPopupAmount;
    this.spine.addChildToSlot('amount', this.amount);
    this.listeners = [];
    this.opened = false;
    this.popupWinClick = false;
    this.stepEpicWin = 2;
    // eslint-disable-next-line no-use-before-define
    this.countup = new CountUp(this.amount);
    gameEvents.on('winPopupAnimEnd', this.winPopupAnimEnd, this);
    gameEvents.on('openWinPopup', this.open, this);
    this.flag = false;
    this.lister = null;
    this.popupCloseTime = null;
    this.autoPlayTimeInterval = null;
  }

  collectWinPopup(isAutoBet = false) {
    this.countup.duration = 0;
    soundService.stop(SoundTypes.WinAccumulationSound);
    const winType = getWinPopupType();
    if (winType === winTypes.EPIC) {
      this.flag = false;
      this.spine.play(`${animationNames[winType]}_win_loop`, true);
    }
    if (winType === winTypes.SUPER) {
      this.flag = false;
      this.spine.play(`${animationNames[winType]}_win_loop`, true);
    }
    if (this.popupWinClick) {
      this.winPopupAnimEnd();
      if (spinBarStore.selectedAutoBetCount && spinBarStore.isAutoBetStart && !isAutoBet) {
        gameMainDrawer.spinBar.handleDoSpin();
      }
    }
    this.popupWinClick = true;
  }

  open(amount, timeScale = 1) {
    this._timeScale = timeScale;
    gameMainDrawer.spinBar.spinButtonCollectMode.visible = true;
    const winType = getWinPopupType();
    this.flag = true;
    if (this.opened) {
      this.accelerate();
      this.amount.text = 2000;
    } else {
      this.popup.interactive = true;
      this.popup.buttonMode = true;
      // if (amount) {
      this.opened = true;
      this.amount.text = winBarStore.winAmount;
      this.countup.start({
        min: 0,
        max: this.amount.text,
        duration: popupsDurationByType[winType],
      });
      soundService.play(SoundTypes.WinAccumulationSound);

      this.popup.on('pointerdown', () => {
        clearTimeout(this.autoPlayTimeInterval);
        // this.popupWinClick = true;
        this.collectWinPopup();
      });
      gameMainDrawer.spinBar.spinButtonCollectMode.on('pointerdown', () => {
        clearTimeout(this.autoPlayTimeInterval);
        // this.popupWinClick = true;
        this.collectWinPopup();
      });
      this.lister = addKeyboardListener(['Enter', ' '], () => {
        // this.popupWinClick = true;
        clearTimeout(this.autoPlayTimeInterval);
        this.collectWinPopup();
      });
      this.popup.visible = true;
      // this.activate();
      this.spine.clearListeners();
      this.blackayer.visible = true;
      soundService.play(popupsSoundsByType[winType]);

      this.stepEpicWin = 2;
      if (winType === winTypes.EPIC) {
        this.spine.play(`${animationNames[this.stepEpicWin]}_win_start`, false);
      } else if (winType === winTypes.SUPER) {
        this.spine.play(`${animationNames[this.stepEpicWin]}_win_start`, false);
      } else {
        this.spine.play(`${animationNames[winType]}_win_start`, false);
      }

      this.spine.addListener(
        'complete',
        () => {
          this.autoPlayTimeInterval = setTimeout(() => {
            if (spinBarStore.selectedAutoBetCount && spinBarStore.isAutoBetStart) {
              // this.collectWinPopup(true);
              this.onCloseEnd();
              gameMainDrawer.spinBar.handleDoSpin();
            }
          }, 3500);
          this.spine.play(`${animationNames[winType]}_win_loop`, true);
          this.stepEpicWin += 1;
          if (winType === winTypes.SUPER && this.flag) {
            this.spine.play(`${animationNames[this.stepEpicWin]}_win_change`, false);
          }
          if (winType === winTypes.EPIC && this.flag) {
            this.spine.play(`${animationNames[this.stepEpicWin]}_win_change`, false);
          }
          if (winType === winTypes.SUPER && this.stepEpicWin === 4) {
            this.flag = false;
            return this.flag;
          }
          if (winType === winTypes.EPIC && this.stepEpicWin === 5) {
            this.flag = false;
            return this.flag;
          }
        },
        this,
        `${animationNames[winType]}_win_start`
      );

      // const winTimer = addToGameTimer(5000, () => {
      //   this.winPopupAnimEnd();
      // });
      // if (this.winClick) {
      //   removeFromGameTimer(winTimer);
      // }
      // this.spine.addListener('complete', this.onCloseEnd, this, true);
      // } else addToGameTimer(0, this.onCloseEnd, this);
    }
  }

  winPopupAnimEnd() {
    const winType = getWinPopupType();
    gameMainDrawer.spinBar.spinButtonCollectMode.visible = false;
    this.spine.clearListeners();
    this.stepEpicWin = 2;
    clearTimeout(this.autoPlayTimeInterval);
    soundService.stop(popupsSoundsByType[winType]);

    this.popupCloseTime = setTimeout(() => {
      this.onCloseEnd();
    }, 200);
    this.lister && this.lister();
    // this.spine.addListener('complete', this.onCloseEnd, this, true);
  }

  activate() {
    this.listeners.push(gameEvents.once('spinStarted', this.accelerate, this));
  }

  fastOpen(amount) {
    this.open(amount, fastTimeScale);
  }

  deActivate() {
    this.listeners.forEach((u) => u());
    this.listeners.length = 0;
  }

  accelerate() {
    // this.spine.accelerateCurrentAnimation(fastTimeScale);
    this.countup.timeScale = fastTimeScale;
  }

  onCloseEnd() {
    clearTimeout(this.popupCloseTime);
    clearTimeout(this.autoPlayTimeInterval);
    this.popupCloseTime = null;
    this.popup.removeAllListeners();
    this.countup.timeScale = 1;
    this.countup.stop();
    this.spine.stop();
    this.deActivate();
    this.opened = false;
    this.popupWinClick = false;
    this.popup.visible = false;
    this.blackayer.visible = false;
    gameEvents.emit('winPopupClosed');
    gameStore.setIsOpenBigWinPopup(false);
  }
}
// eslint-disable-next-line no-use-before-define
export class CountUp extends Animation {
  constructor(target) {
    super(target);
    this.value = 0;
  }

  start(options, fromCurrentPosition) {
    this.fraction = Math.max(digitLength(options.min), digitLength(options.max));
    super.start(options, fromCurrentPosition);
  }

  setter(val) {
    this.value = val;
    val = toFixed(val, this.fraction);
    this.target.text = isoStandard(val);
  }

  getter() {
    return this.value;
  }
}
